// Core
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

// Chakra
import {
  Text,
  useColorModeValue,
  Flex,
  Table,
  Thead,
  Tr,
  Td,
  Tbody,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import Attention from '../../../components/Modals/Attention';
import { DeleteIcon } from '@chakra-ui/icons';
import { parseDateTime, priceToCents } from '../../../services/Utils';
import usePermissionStatusByKey from '../../../hooks/usePermissionStatusByKey';
import {
  priceCodeOptions,
  priceCodeOptionsMap,
} from '../../../constants/prices';
import StationFilterInput from './components/StationFilterInput';

const BasePrice = () => {
  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [basePrices, setBasePrices] = useState([]);
  const [basePricesMeta, setBasePricesMeta] = useState([]);
  const limit = 25;

  const isBasePrices = usePermissionStatusByKey('GET:/admin/base-prices');
  const isDeleteBasePrice = usePermissionStatusByKey(
    'DELETE:/admin/base-prices/:id',
  );
  const [station, setStation] = useState();

  const { isFetching, refetch, isLoading: isBasePricesLoading } = useQuery({
    queryKey: ['base-prices', offset, isBasePrices, station],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (isBasePrices && station?.value) {
        const params = {
          limit,
          offset,
        };

        if (station) {
          params['stationId'] = station.value;
        }

        const response = await apiService.getBasePrices(params);
        setBasePrices(response.data);
        setBasePricesMeta(response.meta);
      }

      return true;
    },
    enabled: !!isBasePrices,
    retry: false,
    refetchInterval: 30000,
  });

  const onNewButtonClicked = useCallback(() => {
    history.push('/base-prices/new');
  }, [history]);

  const { data: systemSettings } = useQuery({
    queryKey: ['system-settings'],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await apiService.getSystemSettings();

      return response.data;
    },
  });

  const isAutomatic = systemSettings?.priceUpdateMode === 'automatic';

  const handleEditOpen = useCallback(
    (plan) => {
      if (!isAutomatic) {
        history.push(`/base-prices/${plan.id}`);
      }
    },
    [history, isAutomatic],
  );

  const [selectedBasePriceId, setSelectedBasePriceId] = useState();

  const handleDelete = useCallback(
    async (basePriceId) => {
      await apiService.deleteBasePrice({ id: basePriceId });
      refetch();
    },
    [refetch],
  );

  return (
    <>
      <Alert status="info" borderRadius="md" mb={4} background="#0E1633">
        <AlertIcon />
        <AlertDescription>
          If scheduled base prices exist, they will become active automatically
          at midnight and replace the current prices.
        </AlertDescription>
      </Alert>

      {isAutomatic && (
        <Alert status="info" borderRadius="md" mb={4} background="#0E1633">
          <AlertIcon />
          <AlertDescription>
            Base price edit is disabled because Price Update Mode set to
            Automatic. You can change it in{' '}
            <Link href="/#/system-settings" color="#0497FD">
              System Settings
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}

      {isBasePrices && (
        <>
          <Flex alignItems="center" gap={2} mb={2}>
            <Text variant="title-lg">Station:</Text>
            <StationFilterInput
              value={station}
              onChange={(station) => setStation(station)}
            />
          </Flex>
          {isBasePricesLoading ? (
            <TableSkeleton rowCount={5} />
          ) : basePrices.length > 0 ? (
            <Card padding={'8px'}>
              <Table color={textColor}>
                <Thead>
                  <Tr>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Name
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Code
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Price
                      </Text>
                    </Td>
                    <Td>
                      <Text variant="title-md" p="0 0 6px 0">
                        Last Updated
                      </Text>
                    </Td>
                    {isDeleteBasePrice && !isAutomatic && (
                      <Td>
                        <Text variant="title-md" p="0 0 6px 0"></Text>
                      </Td>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {basePrices.map((row) => {
                    return (
                      <Tr
                        _last={{
                          '& td': {
                            borderBottom: 'none !important',
                          },
                        }}
                        key={row.id}
                        border="unset"
                        className="cursor-tr"
                      >
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {row?.name}
                          </Text>
                        </Td>
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {priceCodeOptionsMap?.[row?.code]
                              ? `${row?.code} - ${
                                  priceCodeOptionsMap?.[row?.code]
                                }`
                              : row?.code}
                          </Text>
                        </Td>
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {priceToCents(row.price || 0)}
                          </Text>
                        </Td>
                        <Td
                          onClick={() => handleEditOpen(row)}
                          verticalAlign="center"
                          p={paddingTd}
                        >
                          <Text variant="text" color={textColor}>
                            {parseDateTime(row.updatedAt)}
                          </Text>
                        </Td>
                        {isDeleteBasePrice && !isAutomatic && (
                          <Td
                            onClick={() => handleEditOpen(row)}
                            verticalAlign="center"
                            p={paddingTd}
                          >
                            <DeleteIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedBasePriceId(row.id);
                              }}
                              color="#DE3730"
                              fontSize="24px"
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Card>
          ) : (
            <NotFound text="Not Found" />
          )}

          <Pagination
            meta={basePricesMeta ?? { limit, offset, total: 0 }}
            isLoadingPage={isFetching}
            currentPage={Math.floor(offset / limit) + 1}
            onPageChange={(newOffset) => setOffset(newOffset)}
          />
        </>
      )}

      <Attention
        variant="remove"
        handle={() => handleDelete(selectedBasePriceId)}
        isOpen={!!selectedBasePriceId}
        onClose={() => setSelectedBasePriceId(null)}
      />
    </>
  );
};

export default BasePrice;
